<template>
  <div class="row">
    <div v-if="loading" class="col" >
      <span class="loading text-muted">Loading...</span>
    </div>
    <div v-if="!loading" class="col">
      <div class="row">
        <div class="col-6 shippingCostsTable">
          <div class="row header" style="height: 20px; padding-left: 15px;">
            <div class="col-2"></div>
            <div class="col-5"><small class="text-muted">Last 30 Days</small></div>
            <div class="col-5"><small class="text-muted">Monthly Average</small></div>
          </div>
          <shipping-cost-row name="total" label="Total:" v-bind:data="totals.total" />
          <shipping-cost-row name="standard" v-bind:data="totals.standard" />
          <shipping-cost-row name="overnight" v-bind:data="totals.overnight" />
          <shipping-cost-row name="expedited" icon="expeditedOne" v-bind:data="totals.expedited" />
        </div>
        <div class="col-6" >
          <line-chart
            style="height:200px;"
            :chartdata="chartdata"
            :options="options"
            />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue'

import LineChart from '../../components/chart/LineChart.vue'
import BaseIcon from '../../components/icons/BaseIcon.vue'
import ShippingCostRow from './ShippingCostRow.vue'

import apiService from '../../services/api.service'

import { checkResponseErrors } from '../../utils/http'
import moment from 'moment'

export default {
  name: 'ShippingCostWidget',
  data: () => ({
    loading: true,
    totals: null,
    chartdata: null,
    options: null,
  }),
  components: {
    LineChart,
    BaseIcon,
    ShippingCostRow
  },
  async mounted () {
    this.loading = true;

    try {
      const resp = await apiService.get('/dashboard/shippingCost');
      if(await checkResponseErrors(resp)) return;

      // Table
      this.totals = initTotalsData(resp.data.totals)

      // Chart
      var endDate = moment().endOf('day');
      var startDate = moment().startOf('day').subtract(90, 'days');
      this.chartdata = initWidgetData(resp.data, startDate, endDate);
      this.options = {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          display: false,
        },
        scales: {
          xAxes: [{
            type: 'time',
            gridLines: {
              drawOnChartArea: false,
              drawTicks: true,
              color: 'white',
              zeroLineColor: 'white'
            },
            stacked: true,
            time: {
              unit: 'month',
              unitStepSize: 0.5,
              displayFormats: {
                month: 'MMM DD'
              },
              tooltipFormat: 'MM/DD/YYYY',
            },
            ticks: {
              min: startDate,
              max: endDate,
            }
          }],
          yAxes: [{
            id: 'costAxis',
            display: false,
            stacked: true,
            gridLines: {
              drawBorder: false,
              drawOnChartArea: false,
              drawTicks: false
            },
            ticks: {
              fontColor: '#cdcfd1',
              padding: 5,
              maxTicksLimit: 5,
              beginsAtZero: true,
            },
            afterDataLimits(scale) {
              var trendMin = scale.chart.scales.trendAxis.options.ticks.suggestedMin
              var trendMax = scale.chart.scales.trendAxis.options.ticks.suggestedMax
              var costMin = scale.min
              var costMax = scale.max
              if(trendMin != costMin || trendMax != costMax){
                scale.chart.scales.trendAxis.options.ticks.suggestedMin = scale.min;
                scale.chart.scales.trendAxis.options.ticks.suggestedMax = scale.max;
                scale.chart.update();
              }
            }
          },
          {
            id: 'trendAxis',
            ticks: {
              fontColor: '#cdcfd1',
              padding: 25,
              maxTicksLimit: 5,
              beginsAtZero: true,
              callback: (value, index, values) => {
                return formatPrice(value)
              },
            },
            gridLines: {
              drawBorder: false,
              drawOnChartArea: false,
              drawTicks: false
            }
          },
          ]
        },
        tooltips: {
          mode: 'nearest',
          intersect: true,
          callbacks: {
            label: (tooltipItem, data) => {
              var label = data.datasets[tooltipItem.datasetIndex].label || '';

              if (label) {
                  label += ': ';
              }
              label += formatPrice(tooltipItem.yLabel);
              return label;
            }
          }
        },
        plugins: {
          filler: {
            propagate: true
          }
        }
      }

      this.loading = false;
    } catch (error) {
      Vue.toasted.error(error, { duration: 3500 });
      return;
    }
  }, // end mounted
}

const initTotalsData = (data) => {
  data.total.total = formatPrice(data.total.total)
  data.total.monthlyAvg = formatPrice(data.total.monthlyTotal / Math.max(data.total.monthlyCount, 1))
  data.standard.total = formatPrice(data.standard.total)
  data.standard.monthlyAvg = formatPrice(data.standard.monthlyTotal / Math.max(data.standard.monthlyCount, 1))
  data.overnight.total = formatPrice(data.overnight.total)
  data.overnight.monthlyAvg = formatPrice(data.overnight.monthlyTotal / Math.max(data.overnight.monthlyCount, 1))
  data.expedited.total = formatPrice(data.expedited.total)
  data.expedited.monthlyAvg = formatPrice(data.expedited.monthlyTotal / Math.max(data.expedited.monthlyCount, 1))

  return data
}

const initWidgetData = (data, startDate, endDate) => {
  var widgData = {};

  widgData.datasets = []
  // Build Trend Dataset
  if(data.trend.data.some(trend => trend.trend != 0)){
    var trendDataset = {};
    trendDataset.label = "Trend"
    trendDataset.borderColor = "#6bd38f"
    trendDataset.backgroundColor = "#6bd38f"
    trendDataset.fill = false
    trendDataset.borderDash = [3, 3]
    trendDataset.borderWidth = 3
    trendDataset.pointRadius = 0
    trendDataset.pointHitRadius = 3
    trendDataset.lineTension = 0
    trendDataset.yAxisID = 'trendAxis'
    trendDataset.data = []

    data.trend.data.forEach((trendElement) => {
      // Build Trend Points
      var trendPoint = {};
      trendPoint.x = moment(trendElement.date)
      trendPoint.y = trendElement.trend
      trendDataset.data.push(trendPoint)
    })
    widgData.datasets.push(trendDataset)
  }

  // Stacking order
  data.data.sort((dataseta, datasetb) => {return getDatasetOrder(dataseta._id) - getDatasetOrder(datasetb._id)})

  data.data.forEach((element, i) => {
    // Build Chart Dataset
    var widgDataset = {};
    widgDataset.label = element._id
    widgDataset.backgroundColor = getDatasetBackgroundColor(element._id)
    widgDataset.borderColor = getDatasetBackgroundColor(element._id)
    widgDataset.fill = i == 0? 'origin' : '-1';
    widgDataset.pointRadius = 0
    widgDataset.pointHitRadius = 3
    widgDataset.lineTension = 0
    widgDataset.borderJoinStyle = 'bevel'
    widgDataset.yAxisID = 'costAxis'
    widgDataset.data = []

    // Build Dataset Points
    var date = moment(startDate)
    while(date.isSameOrBefore(endDate, 'days')){
      var y = 0
      element.points.forEach((elementPoint) => {
        if(moment(elementPoint.date).isSame(date, 'days')){
          y = elementPoint.cost
        }
      });

      var widgPoint = {};
      widgPoint.x = moment(date)
      widgPoint.y = y 
      widgDataset.data.push(widgPoint)

      date = date.add(1, 'days')
    }
    widgData.datasets.push(widgDataset)
  });

  
  return widgData
}

const getDatasetBackgroundColor = (label) => {
  switch(label){
    case "Expedited":
      return "#2e328e"
    case "Overnight":
      return "#308ac3"
    case "Standard":
    default:
      return "#59d1d6"
  }
}

const getDatasetOrder = (label) => {
  switch(label){
    case "Expedited":
      return 1
    case "Overnight":
      return 2
    case "Standard":
    default:
      return 3
  }
}

const formatPrice = (value) => {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })

  return formatter.format(value)
}
</script>



// WEBPACK FOOTER //
// ShippingCostWidget.vue?675000f2